/* eslint-disable @nx/enforce-module-boundaries */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { LetDirective, PushPipe } from '@ngrx/component';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, debounceTime, fromEvent, map, Subject, take, takeUntil, tap } from 'rxjs';

import { Scopes } from '@ninety/platform/models/entitlements/entitlements-model';
import { Theme } from '@ninety/platform/models/enums/theme';
import { PartnerType } from '@ninety/platform/models/partner-hub/partner-type.enum';
import { FilterService, HelperService } from '@ninety/platform/services';
import {
  CompanyService,
  FeatureFlagFacade,
  FeatureFlagKeys,
  IntercomFeedbackActions,
  MainSidebarIntercomActions,
  selectCompany,
  selectCompanyCreatedAfterPricingGoLive,
  selectIsBillingV2Company,
  selectRedirectToKstGoLiveDate,
  selectCurrentPerson,
  selectHasPartnerHubAccess,
  NavigationActions,
  NavigationSelectors,
  selectUrl,
  AddTeammatesActions,
  selectCurrentUser,
  selectCurrentUserAvatarInfo,
  selectCurrentUserTheme,
  selectIsTrialSubscription,
  selectShouldDisplayTrialCountdown,
  selectLanguage,
  selectDoesUserHaveAccessToOrgChart,
} from '@ninety/platform/state';
import { selectAuthShouldHaveAccess } from '@ninety/platform/state/app-global/auth/auth.selectors';
import { TerraIconModule, TerraIconName } from '@ninety/terra';
import {
  NinetyTooltipDirective,
  IsFeatureAvailablePipe,
  IsTieredPageActivePipe,
  NinetyChipComponent,
  NavigationMenuItemComponent,
  PopoverService,
} from '@ninety/ui/core';
import { UserService } from '@ninety/ui/legacy/core/services/user.service';
import { GuideSelectors } from '@ninety/web/pages/getting-started/guide/_state/guide.selectors';
// eslint-disable-next-line max-len
import { GettingStartedTrackerComponent } from '@ninety/web/pages/getting-started/guide/components/getting-started-tracker/getting-started-tracker.component';
import { INBOX_ENTRY_POINT_STATE_KEY, InboxEntryPoint } from '@ninety/web/pages/inbox/_state/inbox-entry-point';
import { TrialCountdownComponent } from '@ninety/web/pages/settings/company/trial-countdown-component/trial-countdown.component';

import { MainSidebarActions } from '../../_state/main-sidebar/main-sidebar-state.actions';
import { selectIsMainSidebarHidden } from '../../_state/main-sidebar/main-sidebar-state.selectors';
import { CompanyMenuComponent } from '../../components/company-menu/company-menu.component';
import { LearnSupportMenuComponent } from '../learn-support-menu/learn-support-menu.component';
import { UserProfileMenuComponent } from '../user-profile-menu/user-profile-menu.component';

import { sideNavAnimation, sideNavContainerAnimation } from './main-sidenav-animation';

@Component({
  selector: 'ninety-main-sidebar',
  templateUrl: './main-sidebar.component.html',
  styleUrls: ['./main-sidebar.component.scss'],
  animations: [sideNavAnimation, sideNavContainerAnimation],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PopoverService],
  imports: [
    CommonModule,
    RouterModule,
    NavigationMenuItemComponent,
    PushPipe,
    TrialCountdownComponent,
    NinetyTooltipDirective,
    LetDirective,
    CompanyMenuComponent,
    TerraIconModule,
    GettingStartedTrackerComponent,
    NinetyChipComponent,
    IsFeatureAvailablePipe,
    IsTieredPageActivePipe,
    MatListModule,
    MatTooltipModule,
  ],
  host: {
    '(click)': 'backgroundClicked($event)',
    '[class.is-ham-open]': 'openMenuFromHamburgerMenu()',
    '[class.is-resizing]': 'isResizing()',
  },
})
export class MainSidebarComponent implements OnInit, OnDestroy {
  private readonly filterService = inject(FilterService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(UserService);
  private readonly companyService = inject(CompanyService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly featureFlags = inject(FeatureFlagFacade);
  readonly dialog = inject(MatDialog);
  private readonly popoverService = inject(PopoverService);
  private readonly helperService = inject(HelperService);
  private readonly router = inject(Router);

  // Used so that there isn't a transition hiding the nav when the screen size goes below large
  protected isResizing = signal(false);

  readonly Theme = Theme;

  /** Names this link on the navigation, so the inbox can report the surface it was opened from. */
  protected readonly inboxEntryPointState = { [INBOX_ENTRY_POINT_STATE_KEY]: 'mainSidebar' satisfies InboxEntryPoint };

  @ViewChild('learnAndSupportTrigger', { read: ElementRef }) learnAndSupportTrigger: ElementRef<unknown>;
  @ViewChild('userProfileTrigger', { read: ElementRef }) userProfileTrigger: ElementRef<unknown>;
  // Used for navigation tools scrolling
  @ViewChild('navTools') navTools!: ElementRef;

  @Input() isMobile: boolean;
  @Input() showGettingStartedGuide: boolean;

  private readonly destroy$ = new Subject<void>();

  protected readonly url$ = this.store.select(selectUrl);

  isTablet = false;
  isButtonHovering = false;

  public profileMenuIcon: 'more' | 'warning-circle' = 'more';

  protected toolsScrollState$: BehaviorSubject<'TOP' | 'MIDDLE' | 'BOTTOM'> = new BehaviorSubject('TOP');

  protected readonly enableSurveys$ = this.featureFlags.getFlag(FeatureFlagKeys.enableAssessments);
  protected readonly enableSurveysPill$ = this.featureFlags.getFlag(FeatureFlagKeys.enableAssessmentsSidenavPill);
  protected readonly enableReferralProgram$ = this.featureFlags.getFlag(FeatureFlagKeys.referralProgram);
  protected readonly peopleAnalyzerPill$ = this.featureFlags.getFlag(FeatureFlagKeys.webPeopleAnalyzerPill);
  protected readonly showInbox$ = this.featureFlags.getFlag(FeatureFlagKeys.inbox);
  private readonly provideFeedbackFin = toSignal(this.featureFlags.getFlag(FeatureFlagKeys.webProvideFeedbackFin), {
    initialValue: false,
  });
  protected readonly isTrialSubscription$ = this.store.select(selectIsTrialSubscription);

  protected readonly company$ = this.store.select(selectCompany);
  protected readonly currentTheme$ = this.store.select(selectCurrentUserTheme);
  protected readonly currentPerson$ = this.store.select(selectCurrentPerson);
  protected readonly currentUser$ = this.store.select(selectCurrentUser);
  private readonly authShouldHaveAccess$ = this.store.select(selectAuthShouldHaveAccess);
  protected readonly disableNavLinks$ = this.authShouldHaveAccess$.pipe(map(shouldHaveAccess => !shouldHaveAccess));
  protected readonly hasPartnerHubAccess$ = this.store.select(selectHasPartnerHubAccess);
  protected readonly isBillingV2Company$ = this.store.select(selectIsBillingV2Company);
  protected readonly isExpanded$ = combineLatest([
    this.currentUser$,
    this.store.select(NavigationSelectors.selectIsHamburgerMenuOpen),
  ]).pipe(map(([user, isHamburgerOpen]) => user?.settings?.sidenavPinned === 'expanded' || isHamburgerOpen));
  protected readonly isHidden$ = this.store.select(selectIsMainSidebarHidden);
  protected readonly language$ = this.store.select(selectLanguage);
  protected readonly profileMenuIcon$ = this.currentUser$.pipe(
    map((user): TerraIconName => {
      const alerts = user?.settings?.settingsAlerts ?? {};
      const key = Object.keys(alerts).find(f => !!alerts[f]);
      return key ? 'warning-circle' : 'more';
    })
  );

  protected readonly showGettingStartedTracker$ = this.store.select(GuideSelectors.selectIsGuideEnabled);
  protected readonly shouldDisplayTrialCountdown$ = this.store.select(selectShouldDisplayTrialCountdown);
  protected readonly userAvatarInfo$ = this.store.select(selectCurrentUserAvatarInfo);
  // This tells the menu to open/close but also relies on CSS breakpoints
  protected readonly openMenuFromHamburgerMenu = toSignal(
    this.store.select(NavigationSelectors.selectIsHamburgerMenuOpen),
    { initialValue: false }
  );

  // Owned By Reporting
  protected readonly canAccessOrgChart$ = this.store.select(selectDoesUserHaveAccessToOrgChart);

  //Selector for creation after pricing go live
  protected readonly companyCreatedAfterPricingGoLive$ = this.store.select(selectCompanyCreatedAfterPricingGoLive);

  protected readonly redirectToKstGoLiveDate$ = this.store.select(selectRedirectToKstGoLiveDate);

  private containerElement = viewChild<ElementRef>('container');
  private readonly provideFeedbackTooltip = viewChild('provideFeedbackTooltip', { read: MatTooltip });

  constructor() {
    // When hamburger menu opens we always want the scrollbar of nav to be at the top
    effect(() => {
      const isOpening = this.openMenuFromHamburgerMenu;
      const container = this.containerElement;
      if (isOpening()) {
        // Needed so that the correct classes are applied first
        setTimeout(() => {
          if (container()) container().nativeElement.scrollTop = 0;
        });
      }
    });

    fromEvent(window, 'resize')
      .pipe(
        tap(() => {
          this.isResizing.set(true);
        }),
        debounceTime(300),
        tap(() => this.isResizing.set(false)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    // Ad-blocker hint, shown only when Intercom couldn't open
    this.actions$.pipe(ofType(IntercomFeedbackActions.failed), takeUntilDestroyed()).subscribe(() => {
      const tooltip = this.provideFeedbackTooltip();
      if (!tooltip) return;
      tooltip.disabled = false;
      tooltip.show();
      this.cdr.markForCheck();
    });
  }

  ngOnInit() {
    this.isTablet = this.helperService.isTablet();

    this.companyService.companyMasterySettingsUpdated$
      .pipe(
        tap(_ => this.cdr.markForCheck()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSideNav() {
    this.isExpanded$.pipe(take(1)).subscribe(isExpanded => {
      const expanded = !isExpanded;
      this.filterService.sideNavExpanded$.next(expanded);
      const text = expanded ? 'expanded' : 'collapsed';
      this.userService.updateUserSettings$.next({ sidenavPinned: text });
      // Analytics: LayoutAnalyticsEffects maps this to the Segment `main_navigation_toggled` event.
      this.store.dispatch(MainSidebarActions.toggled({ expanded }));
    });
  }

  provideFeedback() {
    const tooltip = this.provideFeedbackTooltip();
    // Flag off: Gainsight PX opens the form; the hint is shown up front in case it's blocked
    if (!this.provideFeedbackFin()) {
      if (tooltip) {
        tooltip.disabled = false;
        tooltip.show();
      }
      return;
    }
    // Clear the ad-blocker hint from an earlier failure; disabling also hides it
    if (tooltip) tooltip.disabled = true;
    this.store.dispatch(MainSidebarIntercomActions.provideFeedbackClicked());
  }

  public openAddInviteDialog() {
    return this.store.dispatch(AddTeammatesActions.openAddTeammatesDialog());
  }

  showLearnAndSupportMenu() {
    this.popoverService.show(this.learnAndSupportTrigger, LearnSupportMenuComponent, [
      {
        originX: 'end',
        originY: 'bottom',
        offsetX: 16,
        overlayX: 'start',
        overlayY: 'bottom',
      },
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'bottom',
      },
    ]);
  }

  showUserProfileMenu() {
    this.disableNavLinks$
      .pipe(
        take(1),
        tap(disabled => {
          this.popoverService.show(this.userProfileTrigger, UserProfileMenuComponent, null, { disabled });
        })
      )
      .subscribe();
  }

  routeTrackedLink(routePath: string, disableNavLinks: boolean, feature: string, label: string) {
    if (
      disableNavLinks ||
      this.router.isActive(routePath, {
        paths: 'subset',
        queryParams: 'subset',
        fragment: 'ignored',
        matrixParams: 'ignored',
      })
    ) {
      return;
    }

    this.store.dispatch(NavigationActions.trackABTestNavigation({ feature, label }));
  }

  protected readonly PartnerType = PartnerType;
  protected readonly NinetyFeatures = Scopes;

  protected backgroundClicked(event: Event) {
    this.preventEventProp(event);
    this.store.dispatch(NavigationActions.closeHamburgerMenu());
  }

  protected preventEventProp(event: Event) {
    event.stopPropagation();
  }
}
